// src/pages/CameraRequests/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Videocam as VideocamIcon,
  PictureAsPdf as PdfIcon,
  LocationOn as LocationIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { cameraFootageRequestsApi, locationsApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import { useIsMobile } from '../../hooks/useResponsive';
import CameraRequestsList from '../../components/CameraRequests/CameraRequestsList';
import CreateRequestForm from '../../components/CameraRequests/CreateRequestForm';
import TreeSelect from '../../components/TreeSelect';
import { usePersistedFilter, FILTER_KEYS } from '../../utils/filterPersistence';

// Helper function to transform locations data for TreeSelect
const transformLocationsForTreeSelect = (locations) => {
  if (!Array.isArray(locations)) return [];

  // Build location tree recursively (same as Tickets.js)
  const buildLocationTree = (locations, parentId = null) => {
    return locations
      .filter((loc) => {
        const locParentId = loc.parentId || loc.parent_id || loc.parentLocation;
        // Handle all falsy values (null, undefined, 0, '', false) as root nodes
        if (parentId === null) {
          return !locParentId || locParentId === 0 || locParentId === '';
        }
        return locParentId === parentId;
      })
      .map((loc) => ({
        id: loc.id || loc._id || loc.location_UUID,
        _id: loc._id || loc.id || loc.location_UUID,
        name: loc.name || loc.friendlyName,
        friendlyName: loc.friendlyName || loc.name,
        locationType: loc.locationType || loc.location_type || 1,
        parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
        children: buildLocationTree(locations, loc.id || loc._id || loc.location_UUID)
      }));
  };

  // Check if data is already in tree format
  const hasChildren = locations.some((loc) => loc.children && Array.isArray(loc.children));

  if (hasChildren) {
    // Already in tree format, just ensure proper field mapping
    return locations.map((loc) => ({
      id: loc.id || loc._id || loc.location_UUID,
      _id: loc._id || loc.id || loc.location_UUID,
      name: loc.name || loc.friendlyName,
      friendlyName: loc.friendlyName || loc.name,
      locationType: loc.locationType || loc.location_type || 1,
      parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
      children: loc.children || []
    }));
  } else {
    // Convert flat list to tree
    const tree = buildLocationTree(locations);
    return tree.length > 0 ? tree : locations;
  }
};

const CameraRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();
  const isMobile = useIsMobile();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();

  // Use persisted filters
  const [currentTab, setCurrentTab] = usePersistedFilter(FILTER_KEYS.CAMERA_REQUEST.CURRENT_TAB, 0);
  const [locationFilter, setLocationFilter] = usePersistedFilter(FILTER_KEYS.CAMERA_REQUEST.LOCATION_FILTER, null);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [stats, setStats] = useState({
    myRequests: 0,
    pendingApproval: 0,
    allRequests: 0
  });

  // Location filter states
  const [locationTreeSelectOpen, setLocationTreeSelectOpen] = useState(false);
  const [locationsForTreeSelect, setLocationsForTreeSelect] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Check permissions
  const canCreate = hasPermission('camera_requests.create');
  const canViewAll = hasPermission('camera_requests.view_all');
  const canApproveSite = hasPermission('camera_requests.approve_site');
  const canApproveDpo = hasPermission('camera_requests.approve_dpo');

  //console.log('[CameraRequests] Permission checks:', {
  //  canCreate,
  //  canViewAll,
  //  canApproveSite,
  //  canApproveDpo,
  //  userRole: user?.role
  //});

  // Load locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationsApi.getAll();

        let locationsData = [];
        if (response?.data) {
          if (Array.isArray(response.data)) {
            locationsData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            locationsData = response.data.data;
          } else if (response.data.locations && Array.isArray(response.data.locations)) {
            locationsData = response.data.locations;
          }
        }

        // Transform locations for TreeSelect component
        const treeSelectLocations = transformLocationsForTreeSelect(locationsData);
        setLocationsForTreeSelect(treeSelectLocations);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };
    loadLocations();
  }, []);

  // Load requests based on current tab
  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      let params = {};

      switch (currentTab) {
        case 0: // My Requests
          params.my_requests = 'true';
          break;
        case 1: // Pending My Approval
          params.pending_my_approval = 'true';
          break;
        case 2: // All Requests (admin/DPO only)
          // No filter needed, API returns all based on permissions
          break;
        default:
          break;
      }

      // Add location filter if set
      if (locationFilter) {
        params.location_id = locationFilter.id;
      }

      const response = await cameraFootageRequestsApi.getAll(params);

      if (response.data.success) {
        let requestsData = response.data.data;

        // Filter by site - when hasSiteFilter is true and siteLocationIds is set (even if empty array for no-access users)
        if (hasSiteFilter && siteLocationIds) {
          requestsData = requestsData.filter(request => {
            const requestLocationId = request.location_id || request.locationId || request.location?.id;
            return siteLocationIds.includes(Number(requestLocationId)) || siteLocationIds.includes(String(requestLocationId));
          });
        }

        setRequests(requestsData);

        // Update stats
        setStats({
          myRequests: requestsData.length,
          pendingApproval: requestsData.filter(r =>
            r.status === 'pending_site_approval' && r.site_manager_email === user?.email
          ).length,
          allRequests: requestsData.length
        });
      }
    } catch (error) {
      console.error('Error loading camera requests:', error);
      enqueueSnackbar(
        error.response?.data?.error || (t ? t('CameraRequests.error_load_requests') : 'Failed to load camera footage requests'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [currentTab, locationFilter, user, enqueueSnackbar, t, hasSiteFilter, siteLocationIds]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCreateRequest = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleRequestCreated = (newRequest) => {
    setOpenCreateDialog(false);
    enqueueSnackbar(t ? t('CameraRequests.success_created') : 'Camera footage request created successfully', { variant: 'success' });
    loadRequests(); // Reload the list
  };

  const handleViewRequest = (requestId) => {
    navigate(`/camera-requests/${requestId}`);
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm(t ? t('CameraRequests.delete_confirm') : 'Are you sure you want to delete this camera footage request? This action cannot be undone.')) {
      return;
    }

    try {
      await cameraFootageRequestsApi.delete(requestId);
      enqueueSnackbar(t ? t('CameraRequests.success_deleted') : 'Camera footage request deleted successfully', { variant: 'success' });
      loadRequests(); // Reload the list
    } catch (error) {
      console.error('Error deleting camera request:', error);
      enqueueSnackbar(
        error.response?.data?.error || (t ? t('CameraRequests.error_delete') : 'Failed to delete camera footage request'),
        { variant: 'error' }
      );
    }
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const handleExportPDF = async () => {
    try {
      setExportingPdf(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

      // Build URL with location filter if set
      let apiUrl = `${API_URL}/api/camera-footage-requests/export/pdf`;
      if (locationFilter) {
        apiUrl += `?location_id=${locationFilter.id}`;
      }

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Important for PDF download
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'camera-requests-audit-report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        t ? t('CameraRequests.pdf_export_success') : 'PDF audit report exported successfully',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      enqueueSnackbar(
        error.response?.data?.error || (t ? t('CameraRequests.pdf_export_error') : 'Failed to export PDF audit report'),
        { variant: 'error' }
      );
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VideocamIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <div>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1">
              {t ? t('CameraRequests.page_title') : 'Camera Footage Requests'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t ? t('CameraRequests.page_subtitle') : 'Manage and track camera footage requests with GDPR compliance'}
            </Typography>
          </div>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: isMobile ? 1 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto'
        }}>
          <Button
            variant={locationFilter ? "contained" : "outlined"}
            startIcon={<FilterIcon />}
            onClick={() => setLocationTreeSelectOpen(true)}
            size={isMobile ? "medium" : "large"}
            color={locationFilter ? "primary" : "inherit"}
            fullWidth={isMobile}
          >
            {locationFilter
              ? locationFilter.friendlyName || locationFilter.name
              : (t ? t('CameraRequests.filter_by_location') : 'Filter by Location')
            }
          </Button>
          {locationFilter && (
            <Button
              variant="outlined"
              onClick={() => setLocationFilter(null)}
              size={isMobile ? "medium" : "large"}
              color="secondary"
              fullWidth={isMobile}
            >
              {t ? t('CameraRequests.clear_filter') : 'Clear Filter'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleExportPDF}
            disabled={exportingPdf}
            size={isMobile ? "medium" : "large"}
            color="secondary"
            fullWidth={isMobile}
          >
            {exportingPdf
              ? (t ? t('CameraRequests.exporting_pdf') : 'Exporting...')
              : (t ? t('CameraRequests.export_pdf_audit') : 'Export PDF')
            }
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateRequest}
              size={isMobile ? "medium" : "large"}
              fullWidth={isMobile}
            >
              {t ? t('CameraRequests.new_request') : 'New Request'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        {t ? t('CameraRequests.workflow_info') : 'All camera footage requests follow a strict approval workflow to ensure GDPR compliance: Requester → Site Manager → DPO → Support Team'}
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t ? t('CameraRequests.tab_my_requests') : 'My Requests'}
                {stats.myRequests > 0 && (
                  <Chip label={stats.myRequests} size="small" color="primary" />
                )}
              </Box>
            }
          />
          {(canApproveSite || canApproveDpo) && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t ? t('CameraRequests.tab_pending_approval') : 'Pending My Approval'}
                  {stats.pendingApproval > 0 && (
                    <Chip label={stats.pendingApproval} size="small" color="warning" />
                  )}
                </Box>
              }
            />
          )}
          {canViewAll && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t ? t('CameraRequests.tab_all_requests') : 'All Requests'}
                  {stats.allRequests > 0 && (
                    <Chip label={stats.allRequests} size="small" />
                  )}
                </Box>
              }
            />
          )}
        </Tabs>
      </Paper>

      {/* Requests List */}
      <Paper sx={{ p: 2 }}>
        <CameraRequestsList
          requests={requests}
          loading={loading}
          onViewRequest={handleViewRequest}
          onDeleteRequest={handleDeleteRequest}
          isAdmin={isAdmin}
          onRefresh={loadRequests}
        />
      </Paper>

      {/* Create Request Dialog */}
      {openCreateDialog && (
        <CreateRequestForm
          open={openCreateDialog}
          onClose={handleCloseCreateDialog}
          onSuccess={handleRequestCreated}
        />
      )}

      {/* TreeSelect for Location Filter */}
      <TreeSelect
        open={locationTreeSelectOpen}
        onClose={() => setLocationTreeSelectOpen(false)}
        onSelect={(location) => {
          setLocationFilter(location);
          setLocationTreeSelectOpen(false);
        }}
        data={locationsForTreeSelect}
        selectedId={locationFilter?.id || locationFilter?._id}
        title={t ? t('CameraRequests.select_location_filter') : 'Select Location to Filter'}
        type="location"
        loading={loadingLocations}
        allowNone={false}
      />
    </Container>
  );
};

export default CameraRequests;
